// Generic detail-page chrome — header strip, optional actions, and a
// content slot organised into named sections. Each list-detail route in
// the backoffice (BookingDetail, CarDetail, VendorDetail, CustomerDetail)
// uses this so the visual rhythm is consistent without per-route boilerplate.

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

interface DetailLayoutProps {
  eyebrow?: string;
  title: string;
  sub?: string;
  backTo?: string;
  backLabel?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function DetailLayout({
  eyebrow,
  title,
  sub,
  backTo,
  backLabel,
  actions,
  children,
}: DetailLayoutProps) {
  return (
    <div className="space-y-5">
      {backTo ? (
        <Link
          to={backTo as never}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
        >
          ← {backLabel ?? 'Back'}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {eyebrow ? (
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
              {eyebrow}
            </div>
          ) : null}
          <h1 className="mt-1 text-[24px] font-semibold tracking-tight text-[color:var(--bo-ink)] sm:text-[28px]">
            {title}
          </h1>
          {sub ? (
            <p className="mt-1 text-[13px] text-[color:var(--bo-mute)]">{sub}</p>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

interface DetailSectionProps {
  title: string;
  children: ReactNode;
  className?: string;
  /** Anchor target, so an error elsewhere can link straight to this section. */
  id?: string;
}

export function DetailSection({ title, children, className, id }: DetailSectionProps) {
  return (
    <section
      id={id}
      className={`rounded-xl border border-[color:var(--bo-line)] bg-white p-5 ${className ?? ''}`}
    >
      <h2 className="mb-3 text-[13px] font-semibold tracking-tight text-[color:var(--bo-ink)]">
        {title}
      </h2>
      {children}
    </section>
  );
}

interface DefRowProps {
  label: string;
  children: ReactNode;
}

export function DefRow({ label, children }: DefRowProps) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-[color:var(--bo-line)] py-2 last:border-0 sm:grid-cols-[180px_1fr]">
      <div className="text-[12px] text-[color:var(--bo-mute)]">{label}</div>
      <div className="text-[13px] text-[color:var(--bo-ink)]">{children}</div>
    </div>
  );
}
